<template>
    <div>
        <ts-page-title :title="$t('monthlyAllowance.pageTitle')" :breadcrumb="[
            { text: $t('home'), href: '/' },
            {
                text: $t('monthlyAllowance.pageTitle'),
                active: true
            }
        ]" />
        <ts-panel>
            <ts-panel-wrapper class="ts-flex ts-items-center ts-justify-between">
                <div class="ts-space-x-2">
                    <Button type="info" ghost @click="addNew">{{
                        $t('addNew')
                    }}</Button>
                    <Button type="success" @click.prevent="onShowExport" :loading="exporting">
                        <i class="fas fa-file-excel"></i> {{ $t('export') }}
                    </Button>
                    <Button type="primary" ghost icon="ios-cloud-upload" @click="importIncentive">
                        {{ $t('import') }}
                    </Button>
                </div>
                <div class="ts-flex ts-space-x-2">
                    <div>
                        <DatePicker type="year" format="yyyy" placeholder="Select Year" @on-change="onChangeYear"
                            :value="year"></DatePicker>
                    </div>
                    <div>
                        <DatePicker type="month" format="MM" placeholder="Select Month" @on-change="onChangeMonth"
                            :value="month">
                        </DatePicker>
                    </div>
                    <div>
                        <Input v-model="search" search :placeholder="$t('monthlyAllowance.searchEmployeeName')
                            " />
                    </div>
                </div>
            </ts-panel-wrapper>
            <Table :columns="columns" :data="resources" size="small" :loading="loading" max-height="700">
                <template slot-scope="{ row }" slot="employee_name_en">
                    {{
                        row.employee_profile
                        ? row.employee_profile.employee_name_en
                        : ''
                    }}
                </template>
                <template slot-scope="{ row }" slot="allowance_item">
                    {{
                        row.pay_item_allowance
                        ? row.pay_item_allowance.allowance_item
                        : ''
                    }}
                </template>
                <template slot-scope="{ row }" slot="allowance">
                    <span class="ts-font-bold text-success">{{
                        formatNumber(row.allowance)
                    }}</span>
                </template>
                <template slot-scope="{ row }" slot="action">
                    <a href="#" class="text-primary" @click.prevent="onEdit(row)" v-tooltip="$t('edit')">
                        <Icon type="ios-create" size="20" />
                    </a>
                    <Poptip confirm :title="$t('areYouSure')" @on-ok="onDelete(row)" :transfer="true" :ok-text="$t('yes')"
                        :cancel-text="$t('no')">
                        <a class="text-danger" v-tooltip="$t('delete')" :disabled="row._deleting">
                            <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
                            <Icon type="ios-trash" size="20" v-else />
                        </a>
                    </Poptip>
                </template>
            </Table>
            <div class="ts-flex ts-items-center ts-justify-end ts-p-4">
                <ts-i-pagination v-model="pagination" @navigate="fetchData({
                    filter: {
                        search: search,
                        cycle_month: month,
                        cycle_year: year
                    }
                })"></ts-i-pagination>
            </div>
            <a-modal v-model="showForm" draggable sticky scrollable :mask="false" :footer="null" :z-index="1022"
                width="700px" :title="$t('monthlyAllowance.pageTitle')">
                <from-action ref="form_action" @cancel="clearEdit" @createSuccess="fetchData({
                    filter: {
                        search: search,
                        cycle_month: month,
                        cycle_year: year
                    }
                })" />
            </a-modal>
            <a-modal v-model="show_export_form" :centered="true" :closable="false" :zIndex="1020" :title="$t('export')"
                :okText="$t('yes')" :cancelText="$t('no')" @ok="onExport" @on-cancel="clearEdit" :confirmLoading="exporting"
                width="450px">
                <ts-loading-banner :loading="export_resource_loading" class="ts-space-y-2">
                    <div class="mb-3">
                        <label class="form-label col-label">{{
                            $t('branchName')
                        }}</label>
                        <Select v-model="model.branch_id" multiple :max-tag-count="1" :filterable="true"
                            :filter-by-label="true">
                            <Option v-for="item in branches" :value="item.branch_id" :key="item.branch_id"
                                :label="item.branch_name_en">
                                {{ item.branch_name_en }}
                            </Option>
                        </Select>
                    </div>
                    <div class="mb-3">
                        <RadioGroup v-model="model.id_type">
                            <Radio class="mt-1" :label="c.value" border v-for="(c, index) in calculateTypes" :key="index">
                                {{ c.label }}
                            </Radio>
                        </RadioGroup>
                    </div>
                    <div class="mb-3" v-if="model.id_type == 1">
                        <div>
                            <label>{{
                                $t('monthlyAllowance.orgChartStructure')
                            }}</label>
                            <Select v-model="model.org_chart_structure_id" filterable :loading="loading"
                                class="from-label" :placeholder="$t('all')" :class="{
                                    'is-invalid': errors.has(
                                        'org_chart_structure_id'
                                    )
                                }" :clearable="true" @on-change="onOrgChange">
                                <Option v-for="(org, index) in orgStructure" :value="org.org_chart_structure_id"
                                    :key="index">
                                    {{ org.org_chart_structure }}
                                </Option>
                            </Select>
                            <div class="invalid-feedback" v-if="errors.has('org_chart_structure_id')">
                                {{ errors.first('org_chart_structure_id') }}
                            </div>
                        </div>
                    </div>
                    <div class="mb-3" v-if="model.id_type == 2">
                        <div>
                            <label>{{
                                $t('monthlyAllowance.shiftName')
                            }}</label>
                            <Select v-model="model.shift_id" filterable :loading="loading" class="from-label" multiple
                                :max-tag-count="2" :placeholder="$t('all')" @on-change="onShiftChange">
                                <Option v-for="(s, index) in shift" :value="s.shift_id" :key="index">
                                    {{ s.shift_name + '-' + s.shift_type }}
                                </Option>
                            </Select>
                            <div class="invalid-feedback" v-if="errors.has('shift_id')">
                                {{ errors.first('shift_id') }}
                            </div>
                        </div>
                    </div>
                    <div class="row ts-mb-6">
                        <div class="col-sm-12">
                            <label class="form-label col-label">{{
                                $t('monthlyAllowance.employee')
                            }}</label>
                            <Select v-model="model.employee_id" filterable remote :remote-method="searchEmployee"
                                :loading="loading" class="from-label" multiple :max-tag-count="2" :placeholder="$t('all')">
                                <Option v-for="(emp, index) in employeeProfile" :value="emp.employee_id" :key="index">
                                    {{
                                        emp.card_id +
                                        '-' +
                                        emp.employee_name_kh +
                                        '-' +
                                        emp.employee_name_en
                                    }}
                                </Option>
                            </Select>
                        </div>
                    </div>
                </ts-loading-banner>
            </a-modal>
            <a-modal v-model="showFormImport" draggable sticky scrollable :mask="false" :footer="null" :z-index="1022"
                :title="$t('monthlyAllowance.importMonthlyAllowance')">
                <ImportMonthlyAllowance ref="import_allowance" @cancel="() => (showFormImport = false)" @fetchData="fetchData({
                    filter: {
                        search: search,
                        cycle_month: month,
                        cycle_year: year
                    }
                })" />
            </a-modal>
        </ts-panel>
    </div>
</template>
<script>
import FromAction from './form.vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import { debounce } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'
import { Errors } from 'form-backend-validation'
import { trim } from 'lodash'
import ImportMonthlyAllowance from './import-monthly-allowance'

export default {
    name: 'monthly-allowance',
    components: {
        FromAction,
        ImportMonthlyAllowance
    },
    data() {
        return {
            loading: false,
            exporting: false,
            show_export_form: false,
            width: 'width: 300px',
            showForm: false,
            export_resource_loading: false,
            showFormImport: false,
            year: moment().format('YYYY'),
            month: moment().format('MM'),
            errors: new Errors(),
            orgStructure: [],
            branches: [],
            shift: [],
            employeeProfile: [],
            model: {
                branch_id: [],
                id_type: 1,
                org_chart_structure_id: null,
                shift_id: [],
                employee_id: []
            },
            calculateTypes: [
                {
                    value: 1,
                    label: 'Organizational Structure'
                },
                {
                    value: 2,
                    label: 'Shift'
                }
            ]
        }
    },
    computed: {
        ...mapState('payroll/monthlyAllowance', ['resources', 'pagination']),
        ...mapGetters(['formatNumber']),
        search: {
            get() {
                return this.$store.state.payroll.monthlyAllowance.search
            },
            set(newValue) {
                this.$store.commit(
                    'payroll/monthlyAllowance/SET_SEARCH',
                    newValue
                )
                this.$store.commit(
                    'payroll/monthlyAllowance/RESET_CURRENT_PAGE'
                )
            }
        },

        columns() {
            return [
                {
                    title: this.$t('monthlyAllowance.employeeName'),
                    slot: 'employee_name_en',
                    sortable: true
                },
                {
                    title: this.$t('monthlyAllowance.allowance'),
                    slot: 'allowance_item',
                    sortable: true
                },
                {
                    title: this.$t('monthlyAllowance.allowanceAmount'),
                    slot: 'allowance',
                    align: 'center',
                    sortable: true
                },
                {
                    title: this.$t('monthlyAllowance.cycleMonthAndYear'),
                    key: 'cycle_month_and_year',
                    align: 'center',
                    sortable: true,
                    width: '200'
                },
                {
                    title: this.$t('monthlyAllowance.description'),
                    key: 'description',
                    align: 'center',
                    sortable: true
                },
                {
                    title: this.$t('monthlyAllowance.importCode'),
                    key: 'import_code',
                    sortable: true
                },
                {
                    title: this.$t('actions'),
                    key: 'action',
                    slot: 'action',
                    align: 'center',
                    fixed: 'right',
                    className: 'ts-space-x-2',
                    width: 90
                }
            ]
        }
    },
    methods: {
        ...mapActions('payroll/monthlyAllowance', ['formViewModel']),

        addNew() {
            this.showForm = true
            // this.$refs.form_action.fetchResource()
            // this.$refs.form_action.clearInput()
        },
        onShowExport() {
            this.show_export_form = true
            this.fetchBranch()
            this.fetchExportResource({
                fnName: 'orgStructure,shift,employeeProfile'
            })
            this.clearInput()
        },
        importIncentive() {
            this.showFormImport = true
            this.$refs.import_allowance.fetchResource()
            // this.$refs.import_allowance.clearInput()
        },
        clearEdit() {
            this.showForm = false
            this.$store.commit('payroll/monthlyAllowance/SET_EDIT_DATA', {})
            this.$refs.form_action.clearInput()
        },
        fetchData(attributes) {
            this.loading = true
            this.$store
                .dispatch('payroll/monthlyAllowance/fetch', attributes)
                .catch(error => {
                    this.loading = false
                    this.notice({
                        type: 'error',
                        text: error.message
                    })
                })
                .finally(() => {
                    this.loading = false
                })
        },
        onEdit(record) {
            this.showForm = true
            this.$store.commit('payroll/monthlyAllowance/SET_EDIT_DATA', record)
            this.$refs.form_action.fetchResource()
            this.$refs.form_action.setEditData()
        },
        onDelete(record) {
            record._deleting = true
            this.$store
                .dispatch('payroll/monthlyAllowance/destroy', record.record_id)
                .then(response => {
                    this.fetchData({
                        filter: {
                            search: this.search,
                            cycle_month: this.month,
                            cycle_year: this.year
                        }
                    })
                    record._deleting = false
                    this.notice({
                        type: response.message_id == 0 ? 'success' : 'warning',
                        text: response.message
                    })
                })
                .catch(error => {
                    this.notice({ type: 'error', text: error.message })
                    record._deleting = false
                })
        },
        onExport() {
            this.exporting = true
            this.errors = new Errors()
            this.$store
                .dispatch('payroll/monthlyAllowance/exportExcel', this.model)
                .then(response => {
                    let filename = trim(
                        response.headers['content-disposition']
                            .substring(
                                response.headers['content-disposition'].indexOf(
                                    'filename'
                                )
                            )
                            .replace('filename=', ''),
                        '"'
                    )
                    FileDownload(response.data, filename)
                    this.show_export_form = false
                })
                .catch(error => {
                    this.errors = new Errors(error.errors)
                    this.notice({ type: 'error', text: error.message })
                })
                .finally(() => {
                    this.exporting = false
                })
        },
        async fetchExportResource(attributes) {
            this.export_resource_loading = true
            let response = await this.formViewModel({
                params: attributes
            })
            if (attributes.fnName) {
                attributes.fnName.split(',').forEach(fn => {
                    this[fn] = response[fn]
                })
            }
            this.export_resource_loading = false
        },
        fetchBranch() {
            this.loading = true
            this.$store
                .dispatch('security/user/getBranchAssigned')
                .then(response => {
                    this.branches = response.data
                })
                .catch(error => {
                    this.notice({ type: 'error', text: error.message })
                })
                .finally(() => {
                    this.loading = false
                })
        },
        async onOrgChange(value) {
            this.model.employee_id = []
            this.fetchExportResource({
                fnName: 'employeeProfile',
                org_chart_structure_id: value
            })
        },
        async onShiftChange(value) {
            this.model.employee_id = []
            this.fetchExportResource({
                fnName: 'employeeProfile',
                shift_id: value
            })
        },
        searchEmployee: debounce(async function (query) {
            if (this.employeeProfile.length > 0) {
                var avoidSearch = this.employeeProfile.find(
                    emp =>
                        emp.card_id +
                        '-' +
                        emp.employee_name_kh +
                        '-' +
                        emp.employee_name_en ==
                        query.trim()
                )
                if (avoidSearch) return
            }
            this.fetchExportResource({
                fnName: 'employeeProfileSearch',
                emp_search: query,
                org_chart_structure_id: this.model.org_chart_structure_id,
                shift_id: this.model.shift_id,
                branch_id: this.model.branch_id
            })
        }, 500),
        onChangeYear(year) {
            this.year = year
            this.fetchData({
                filter: {
                    search: this.search,
                    cycle_year: year,
                    cycle_month: this.month
                }
            })
        },
        onChangeMonth(month) {
            this.month = month
            this.fetchData({
                filter: {
                    search: this.search,
                    cycle_month: month,
                    cycle_year: this.year
                }
            })
        },
        clearInput() {
            this.errors = new Errors()
            this.model.branch_id = null
            this.model.org_chart_structure_id = null
            this.model.shift_id = null
            this.model.employee_id = null
        },
        notice(not) {
            this.$Notice[not.type]({
                title: 'MONTHLY ALLOWANCE',
                desc: not.text
            })
        }
    },
    watch: {
        search: debounce(function (search) {
            this.fetchData({
                filter: {
                    search: search,
                    cycle_year: this.year,
                    cycle_month: this.month
                }
            })
        }, 500)
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.fetchData({
                filter: {
                    search: vm.search,
                    cycle_year: vm.year,
                    cycle_month: vm.month
                }
            })
        })
    }
}
</script>
